import React from "react"
import { graphql } from "gatsby"
import MainLayout from "@/layouts/MainLayout"
import SanityContentWrapper from "@/components/SanityContentWrapper"
import SharedHead from "@/components/SharedHead"
import SupportHeader from "@/components/SupportHeader"
import { useTranslation, useI18next } from "@zestia/gatsby-plugin-react-i18next"
import SupportTopicMobileNav from "@/components/SupportTopicMobileNav"
import SupportTopicSidebarNav from "@/components/SupportTopicSidebarNav"
import SupportArticlePlans from "@/components/SupportArticlePlans"
import ArticleNavigation from "@/components/ArticleNavigation"
import ScrollSpy from "@/components/ScrollSpy"
import Longform from "@/components/Longform"
import getSanitySubHeadings from "@/utils/get-sanity-sub-headings"
import getSanityVideoSource from "@/utils/get-sanity-video-source"
import SupportArticleAssetWrapper from "@/components/SupportArticleAssetWrapper"
import SanityImage from "gatsby-plugin-sanity-image"
import LazyVideo from "@/components/LazyVideo"
import PlanType from "@/components/PlanType"
import CapsuleTag from "@/components/CapsuleTag"
import UserInterfaceIcon from "@/components/UserInterfaceIcon"
import GuidedZaps from "@/components/GuidedZaps"
import Supported2faApps from "@/components/Supported2faApps"
import SupportedBrowsers from "@/components/SupportedBrowsers"
import {
  ContentSwitcher,
  ContentSwitcherTab,
} from "@/components/ContentSwitcher"
import ActionSteps, { ActionStep } from "@/components/ActionSteps"

const sanityContentWrapperOverrides = {
  typeOverrides: {
    image: ({ value }) => (
      <SupportArticleAssetWrapper>
        <SanityImage
          {...value}
          width={700}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </SupportArticleAssetWrapper>
    ),
    inlineVideo: ({ value }) => (
      <SupportArticleAssetWrapper>
        <LazyVideo video={getSanityVideoSource(value)} alt={value.alt} />
      </SupportArticleAssetWrapper>
    ),
    userInterfaceIcon: ({ value }) => {
      console.log(value)
      return <UserInterfaceIcon src={value.name} />
    },
    guidedZaps: ({ value }) => <GuidedZaps ids={value.ids} />,
    supported2faApps: () => <Supported2faApps />,
    supportedBrowsers: () => <SupportedBrowsers />,
    contentSwitcher: ({ value }) => {
      return (
        <ContentSwitcher>
          {value.content.map(tab => {
            return (
              <ContentSwitcherTab name={tab.name} isDefault={tab.isDefault}>
                {/* These can have any valid support content inside (such as more components), so we need to have nested sanity content wrappers */}
                <SanityContentWrapper {...sanityContentWrapperOverrides}>
                  {tab.content}
                </SanityContentWrapper>
              </ContentSwitcherTab>
            )
          })}
        </ContentSwitcher>
      )
    },
    actionSteps: ({ value }) => {
      const { steps } = value

      return (
        <ActionSteps>
          {steps.map(step => (
            <ActionStep
              text={step.text}
              icon={step.icon?.name}
              hoverImage={step.hoverImage}
            />
          ))}
        </ActionSteps>
      )
    },
  },
  markOverrides: {
    planType: ({ text }) => {
      return <PlanType>{text}</PlanType>
    },
    capsuleTag: ({ text }) => {
      return <CapsuleTag>{text}</CapsuleTag>
    },
  },
}

export const Head = props => {
  const { hidden, title, description, canonicalUrl } =
    props.data.sanitySupportDoc

  return (
    <SharedHead
      {...props}
      title={title}
      description={description}
      appendTitle={true}
      noIndex={hidden}
      canonicalUrl={canonicalUrl}
    />
  )
}

const SanitySupportDocLayout = props => {
  const { t } = useTranslation()

  const { allSanitySupportDoc, sanitySupportDoc } = props.data

  const {
    title,
    hideSidebar,
    hideTargets,
    targetRoles,
    targetPlans,
    _rawContent,
  } = sanitySupportDoc

  const otherSupportArticles = allSanitySupportDoc.nodes
    .filter(doc => doc.sortOrder > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder)

  // Create an array of subheadings for the article navigation
  const subHeadings = getSanitySubHeadings(sanitySupportDoc)

  // Get translation metadata and create availableSlugs array (optional)
  // TODO: Make this into a shared util of some sort
  const translationMetaDataForThisDocument =
    props.data.allSanityTranslationMetadata.nodes.find(node => {
      return (
        node.translations.length > 0 &&
        node.translations.find(
          translation => translation.value?._id === props.pageContext.id,
        )
      )
    })

  const availableSlugs = translationMetaDataForThisDocument?.translations.map(
    translation => {
      return {
        lang: translation.value.language,
        slug: translation.value.slug.current, // Currently support docs make the content editor enter the full slug (e.g. /support/setup-and-configuration/getting-started) so we don't need to mess with it here
      }
    },
  )

  return (
    <MainLayout
      navBarClassName="nav-bar--support"
      availableSlugs={availableSlugs ?? []}
      trackingData={{
        content_category: "support",
      }}
    >
      <SupportHeader />

      {!hideSidebar && (
        <SupportTopicMobileNav className="support-topic-mobile-nav--article" />
      )}

      <div className="support-article">
        <section className="container-fluid">
          <div className="row">
            <div className="col-md-3">
              {!hideSidebar && (
                <SupportTopicSidebarNav articles={otherSupportArticles} />
              )}
            </div>

            <div
              className={`${
                hideSidebar ? "col-xs-12 mt-16" : "col-xs-12 col-md-6"
              }`}
            >
              {!hideSidebar && (
                <ArticleNavigation links={subHeadings} mobile={true} />
              )}

              <div className="support-portal__article-container mb-16">
                <header className="article-title">
                  <h1
                    className="h3 mb-10"
                    id={title.toLowerCase().replaceAll(" ", "-")}
                  >
                    {title}
                  </h1>
                </header>

                {!hideTargets && (
                  <div className="support-article-meta mb-10">
                    <div className="support-article-meta__col pt-5 pb-5">
                      <span className="support-article-meta__description">
                        {t("support.who-can-use-this-feature")}
                      </span>

                      {t(`support.roles.${targetRoles ?? "anyone"}`)}
                    </div>

                    <div className="support-article-meta__col pt-5 pb-5">
                      <span className="support-article-meta__description">
                        {t("support.available-on-plans")}
                      </span>

                      <span className="support-article-meta__tag">
                        <SupportArticlePlans plans={targetPlans} />
                      </span>
                    </div>
                  </div>
                )}

                {/* Note: The `.crawler-article-content` class is used by the support search indexer */}
                <Longform className="pb-5">
                  <ScrollSpy
                    className="crawler-article-content"
                    options={{ limitToQuerySelector: "h2", offset: 80 }}
                  >
                    <SanityContentWrapper {...sanityContentWrapperOverrides}>
                      {_rawContent}
                    </SanityContentWrapper>
                  </ScrollSpy>
                </Longform>
              </div>
            </div>

            {!hideSidebar && (
              <div className="col-sm-2 col-sm-offset-1">
                <ArticleNavigation links={subHeadings} mobile={false} />
              </div>
            )}
          </div>
        </section>
      </div>
    </MainLayout>
  )
}

export const query = graphql`
  query ($id: String!, $language: String, $topic: String) {
    ...Locales

    allSanityTranslationMetadata {
      nodes {
        translations {
          value {
            ... on SanitySupportDoc {
              _id
              language
              slug {
                current
              }
            }
          }
        }
      }
    }

    allSanitySupportDoc(
      filter: { language: { eq: $language }, topic: { eq: $topic } }
    ) {
      nodes {
        title
        slug {
          current
        }
        sortOrder
      }
    }

    sanitySupportDoc(_id: { eq: $id }) {
      _id
      _rawContent
      title
      topic
      hideTargets
      hideSidebar
      hidden
      language
      targetPlans
      targetRoles
      sortOrder
      isNew
      description
      canonicalUrl
      slug {
        current
      }
      internal {
        type
      }
    }
  }
`

export default SanitySupportDocLayout
